import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React from 'react';
import RedirectIcon from '../../public/img/icons/icon_redirect.svg';
import AmplitudeSendEvent from '../../src/hooks/useAmplitude';
import { useSessionStorage } from '../../src/hooks/useSessionStorage';
import { VersionsData } from '@/src/services/getVersionsData';
import { useAffise } from '../../src/utils/affise';
import SignupWrapper from '../common/SignupWrapper';
import SubmitInfo from '../common/SubmitInfo';
import { useContextState } from '../context';

interface SignupFormProps {
  onChange?: () => void;
  onSubmit?: () => void;
  versionsData: VersionsData;
}

const SignupForm: React.FC<SignupFormProps> = ({ onChange, onSubmit, versionsData }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const {
    authorized,
    notification: { text: errorText, global },
  } = useContextState();

  const current = versionsData.current;
  const beta = versionsData.beta;

  return (
    <div className="l">
      <div className="head">
        <div className="big-title">
          <h1>{t('download_welcome_header')}</h1>
        </div>
        {t('download_welcome_description_auth')}
      </div>
      {/*{!authorized && (*/}
      {/*  <>*/}
      {/*    <div className="input-label">{t('download_welcome_form_header')}</div>*/}
      {/*    <div className="input">*/}
      {/*      <input*/}
      {/*        type="email"*/}
      {/*        placeholder={t('download_welcome_form_placeholder')}*/}
      {/*        onChange={onChange}*/}
      {/*        id="8143"*/}
      {/*      />*/}
      {/*    </div>*/}
      {/*    {errorText && !global && (*/}
      {/*      <div className="subinput-info red">{t('notification_error_email')}</div>*/}
      {/*    )}*/}
      {/*  </>*/}
      {/*)}*/}
      <div className="submit">
        <div className="submit-l">
          <button type="button" className="btn btn-primary" onClick={onSubmit} id="8543">
            <span>{t('download_welcome_form_button')}</span>
          </button>
        </div>
        <div className="submit-r">
          {current ? (
            <p>
              {t('download_welcome_form_version')} {current.version}
              <br />({current.build} {t('core.since')} {current.date})
            </p>
          ) : (
            <p>
              {t('download_welcome_form_version')} {t('download_version')}
              <br />({t('download_version_build')} {t('core.since')} {t('download_version_date')})
            </p>
          )}
        </div>
      </div>

      {beta && (
        <div className="submit">
          <div className="submit-l">
            <a
              href={beta.downloadUrl}
              className="btn btn-compact btn-secondary btn-secondary--no-borders"
              onClick={(e) => {
                e.preventDefault();
                window.open(beta.downloadUrl, '_blank');
                router.push('/terminal/how-to-setup');
              }}
            >
              <span>{t('download_welcome_form_button_beta')}</span>
            </a>
          </div>
          <div className="submit-r submit-r--secondary">
            <p>
              {t('download_welcome_form_version_beta')} {beta.version}
              <br />({beta.build} {t('core.since')} {beta.date})
            </p>
          </div>
        </div>
      )}

      <SubmitInfo key1="submitInfo_part1" key2="submitInfo_part2" />
    </div>
  );
};

const Download = ({ versionsData }: { versionsData: VersionsData }) => {
  const { t } = useTranslation();
  const { set } = useSessionStorage();
  const router = useRouter();
  const { sendAffiseEvent } = useAffise();

  const {
    user: { email },
  } = useContextState();

  const current = versionsData.current;

  return (
    <div className="content">
      <div className="download-page">
        <SignupWrapper
          notification={{
            text: t('notification_error_email'),
            color: 'red',
            global: false,
          }}
          isAuthRedundant={true}
          sideeffect={() => {
            sendAffiseEvent('Registration');
            const url = current?.downloadUrl || '/terminal/download/TigerTradeSetup.exe';
            window.open(url, '_blank');
            router.push('/terminal/how-to-setup');
            set('downloadNotification', true);
            set('downloadUrl', url);
            AmplitudeSendEvent({ event_type: 'Download', email });
          }}
        >
          <SignupForm versionsData={versionsData} />
        </SignupWrapper>
        <div className="r">
          <div className="info">
            <div className="col wide">
              <div className="info-title">{t('download_system_title')}</div>
              <p>{t('download_system_os1')}</p>
              <p>{t('download_system_requirements')}</p>
            </div>
            <div className="col wide latest">
              <div className="info-title">{t('previous_versions_latest-title')}</div>
              {current ? (
                <p>
                  {current.version} ({current.date})
                </p>
              ) : (
                <p>
                  {t('download_version')} ({t('download_version_date')})
                </p>
              )}
              <Link passHref href={t('download_platform-updates-link')}>
                <a target="_blank">
                  {t('download_platform-updates-text')}
                  <RedirectIcon className="foot-redirect-icon" />
                </a>
              </Link>
              <Link href="/terminal/download/previous-versions">
                {t('download_download-previous-versions')}
              </Link>
            </div>
            <div className="col">
              <div className="info-title">{t('download_connections')}</div>
              <p>
                {t('download_connections_chapter1')}
                <br />
                {t('download_connections_chapter2')}
                <br />
                {t('download_connections_chapter3')}
                <br />
                {t('download_connections_chapter4')}
                <br />
                {t('download_connections_chapter5')}
                <br />
                {t('download_connections_chapter6')}
                <br />
                {t('download_connections_chapter7')}
              </p>
            </div>
            <div className="col">
              <div className="info-title">{t('download_supported')}</div>
              <p>
                {t('download_supported_chapter2')}
                <br />
                {t('download_supported_chapter3')}
                <br />
                {t('download_supported_chapter4')}
                <br />
                {t('download_supported_chapter5')}
                <br />
                {t('download_supported_chapter6')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Download;
